import React, { useState, useEffect, useRef } from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import KeyboardedInput from 'react-touch-screen-keyboard';

import { usePrevious } from '@common/hooks/previous';

import 'react-touch-screen-keyboard/lib/Keyboard.scss';

import './style.scss';

const languageMap = {
  rus: 'ru',
  fra: [
    ['a', 'z', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p'],
    ['q', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l', 'm'],
    ['w', 'x', 'c', 'v', 'b', 'n'],
  ],
  ger: 'de',
  eng: 'us',
};

const KeyboardInput = ({
  value,
  lang = 'eng',
  type = 'text',
  onChange = () => { },
  onBlur = () => { },
  clearSearchField = () => { },
  showKeyboard = false,
  setFocus = false,
  className = '',
  placeholder,
  readOnly = false,
  disableSearchIcon = false,
  enableClearButton = false,
  hasResetButton = false,
  name = 'myInput',
  casino = false,
  disabled = false,
}) => {
  const [text, setText] = useState(value || '');
  const prevText = usePrevious(text);
  const ref = useRef(null);
  const wrapperRef = useRef(null);
  const cursorPosition = useRef();
  const [isReadonly, setIsReadonly] = useState(true);

  const mappedLang = languageMap[lang] || languageMap.eng;

  const updateCursorPosition = (oldVal = '', newVal = '') => {
    const count = newVal.length - oldVal.length;

    // Magic grdonn!!! don't repeat at home
    setTimeout(() => {
      requestAnimationFrame(() => {
        const nextPosition = cursorPosition.current + count;
        ref.current?.input?.setSelectionRange(nextPosition, nextPosition);
        cursorPosition.current = nextPosition;
      });
    }, 0);
  };

  const handleChange = (newText) => {
    if (disabled) return;

    const prevValue = _.isNil(value) ? prevText : value;

    const newTextFormatted = newText.trimStart(' ');

    onChange?.(newTextFormatted);

    if (casino && window.liveCasinoEventSource) {
      window.liveCasinoEventSource.postMessage({ action: 'writeNickName', text: newTextFormatted }, '*');
    }

    if (value === undefined) {
      setText(newTextFormatted);
    }

    updateCursorPosition(prevValue, newTextFormatted);
  };

  const clearInputField = () => {
    clearSearchField?.();
    onBlur?.(false);
    if (value === undefined) {
      setText('');
    }
  };

  const handleResetClick = () => {
    onChange?.('');
    setText('');
  };

  useEffect(() => {
    if (disabled) return;

    if (showKeyboard && ref.current) {
      ref.current.focus();
    }

    if (setFocus) {
      const inputElement = document.querySelector(`.${className || 'input-field-v single-form-item'}`);
      if (inputElement) {
        setIsReadonly(false);
        inputElement.focus();
      }
    }
  }, [showKeyboard, setFocus, className, ref, disabled]);

  useEffect(() => {
    if (value !== undefined) {
      setText(value);
    }
  }, [value]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        onBlur?.(false);
      }
    };

    document.addEventListener('click', handleOutsideClick);
    return () => {
      document.removeEventListener('click', handleOutsideClick);
    };
  }, [wrapperRef]);

  useEffect(() => {
    if (disabled) return;

    if (ref.current && isReadonly) {
      ref.current.input?.addEventListener('focus', () => setIsReadonly(false));
    }
  }, [ref.current, isReadonly, disabled]);

  useEffect(() => {
    if (disabled) return;

    const { input } = ref.current || {};

    input.setAttribute('autocomplete', 'off');

    const getCursorPosition = () => {
      if (!input) return;

      const { selectionStart, selectionEnd } = input;

      cursorPosition.current = Math.max(selectionStart, selectionEnd);
    };

    input?.addEventListener('mouseup', getCursorPosition);
    input?.addEventListener('keyup', getCursorPosition);

    return () => {
      input?.removeEventListener('mouseup', getCursorPosition);
      input?.removeEventListener('keyup', getCursorPosition);
    };
  }, [ref, disabled]);

  return (
    <div className={`header-input-case ${className || ''} ${disabled ? 'disabled' : ''}`} ref={wrapperRef}>
      {text && hasResetButton && (
        <i className="global-icon icon-circle-close reset-btn" onClick={handleResetClick} />
      )}
      <KeyboardedInput
        enabled={!disabled}
        readOnly={disabled || readOnly || isReadonly}
        type={type}
        onChange={handleChange}
        value={text}
        name={_.uniqueId('never-autofill-')}
        placeholder={placeholder}
        defaultKeyboard={mappedLang}
        secondaryKeyboard="us"
        isDraggable={false}
        isFirstLetterUppercase={false}
        ref={ref}
        showKeyboard={showKeyboard && !disabled}
        inputAutoComplete="off"
        inputClassName={className || 'ticketBarcode-field-v single-form-item'}
      />
      {!disableSearchIcon && <span className="global-icon icon-slim-search" />}
      {enableClearButton && <i className="icon-closed" onClick={clearInputField} />}
    </div>
  );
};

KeyboardInput.propTypes = {
  type: PropTypes.string,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  clearSearchField: PropTypes.func,
  showKeyboard: PropTypes.bool,
  setFocus: PropTypes.bool,
  className: PropTypes.string,
  lang: PropTypes.string,
  placeholder: PropTypes.string,
  readOnly: PropTypes.bool,
  disableSearchIcon: PropTypes.bool,
  enableClearButton: PropTypes.bool,
  hasResetButton: PropTypes.bool,
  casino: PropTypes.bool,
  value: PropTypes.string,
  name: PropTypes.string,
  disabled: PropTypes.bool,
};

export default KeyboardInput;
